import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Text,
    Title,
    Alert,
    Table,
    Paper,
    Button,
    Badge,
    Collapse,
    ActionIcon,
    Group,
    useMantineColorScheme,
} from '@mantine/core';
import { IconRefresh, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import { getSingularLabel } from '../../utils/pluralizationUtils';
import { debugLog } from '../../utils/debugLog';
import { TABLE_SCROLL_MIN_WIDTH } from '../../theme/mantineTheme';
import { EmptyState } from '../common/EmptyState';
import { LoadingState } from '../common/LoadingState';

interface FunctionCatalogManagementProps {
    authResolved?: boolean;
    /** When set (e.g. from #admin/functionCatalog/Id), expand and highlight that item */
    selectedEntityId?: string | null;
}

export const FunctionCatalogManagement: React.FC<FunctionCatalogManagementProps> = ({
    authResolved = false,
    selectedEntityId = null
}) => {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const [functionCatalog, setFunctionCatalog] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Refs to prevent multiple calls
    const isLoadingRef = useRef(false);
    const hasLoadedInitialData = useRef(false);
    const previousAuthResolvedRef = useRef<boolean | null>(null);

    const fetchFunctionCatalog = async () => {
        // Prevent multiple simultaneous calls
        if (isLoadingRef.current) {
            debugLog('⏭️ FunctionCatalogManagement: Load already in progress, skipping duplicate request');
            return;
        }

        isLoadingRef.current = true;
        setLoading(true);
        setError(null);
        try {
            debugLog('🔧 FunctionCatalogManagement: Fetching function catalog...');
            const data = await entityService.getFunctionCatalog();
            setFunctionCatalog(data);
        } catch (err) {
            console.error('❌ FunctionCatalogManagement: Error fetching function catalog:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch function catalog');
        } finally {
            isLoadingRef.current = false;
            setLoading(false);
        }
    };

    // Initial load effect - only run once when auth is resolved
    useEffect(() => {
        // Only run if authResolved actually changed
        if (previousAuthResolvedRef.current === authResolved) {
            return;
        }
        previousAuthResolvedRef.current = authResolved;

        // Reset the ref if auth is no longer resolved
        if (!authResolved) {
            hasLoadedInitialData.current = false;
            return;
        }

        // Prevent multiple loads
        if (hasLoadedInitialData.current || isLoadingRef.current) {
            debugLog('⏭️ FunctionCatalogManagement: Already loaded or loading, skipping initial load');
            return;
        }

        debugLog('🔄 FunctionCatalogManagement: Initial load triggered by auth resolution');
        hasLoadedInitialData.current = true;
        fetchFunctionCatalog();
    }, [authResolved]);

    const handleRefresh = async () => {
        hasLoadedInitialData.current = false;
        await fetchFunctionCatalog();
    };

    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());

    const toggleRowExpansion = (index: number) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedRows(newExpanded);
    };

    // Deep link: when selectedEntityId is set, expand that row and scroll to it
    useEffect(() => {
        if (!selectedEntityId || !functionCatalog.length) return;
        const index = functionCatalog.findIndex((item) => (item.Id || item.id) === selectedEntityId);
        if (index >= 0) {
            setExpandedRows((prev) => new Set(prev).add(index));
            setTimeout(() => {
                const rowEl = rowRefs.current.get(index);
                if (rowEl) rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 100);
        }
    }, [selectedEntityId, functionCatalog]);

    const handleRowClick = (item: any) => {
        const id = item.Id || item.id;
        if (id) window.location.hash = `#admin/functionCatalog/${id}`;
    };

    const inputBoxBg = isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-1)';

    const renderInputs = (inputs: any[] | undefined) => {
        if (!inputs || !Array.isArray(inputs) || inputs.length === 0) {
            return <Text size="sm" c="dimmed">No inputs</Text>;
        }

        return (
            <Box>
                {inputs.map((input, idx) => (
                    <Box
                        key={idx}
                        mb="xs"
                        p="xs"
                        style={{
                            backgroundColor: inputBoxBg,
                            borderRadius: 4
                        }}
                    >
                        <Text size="sm" fw={600}>
                            {input.Name || 'Unnamed Input'}
                        </Text>
                        <Box ml="xs" mt={4}>
                            <Text size="xs" c="dimmed">
                                Type: {input.InputType || 'N/A'}
                            </Text>
                            {input.Required !== undefined && (
                                <Badge
                                    size="sm"
                                    color={input.Required ? 'red' : 'gray'}
                                    ml="xs"
                                    variant="light"
                                >
                                    {input.Required ? 'Required' : 'Optional'}
                                </Badge>
                            )}
                            {input.MaxLength && (
                                <Text size="xs" c="dimmed" display="block">
                                    Max Length: {input.MaxLength}
                                </Text>
                            )}
                            {input.Array !== undefined && (
                                <Badge size="sm" variant="light" ml="xs">
                                    {input.Array ? 'Array' : 'Single'}
                                </Badge>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderTargetEntities = (targetEntities: string[] | undefined) => {
        if (!targetEntities || !Array.isArray(targetEntities) || targetEntities.length === 0) {
            return <Text size="sm" c="dimmed">No target entities</Text>;
        }

        return (
            <Group gap={4} wrap="wrap">
                {targetEntities.map((entity, idx) => (
                    <Badge key={idx} size="sm" variant="light">
                        {entity}
                    </Badge>
                ))}
            </Group>
        );
    };

    const rowHighlightBg = selectedEntityId
        ? (isDark ? 'var(--mantine-color-default-hover)' : 'var(--mantine-color-gray-1)')
        : undefined;

    return (
        <Box p="xl">
            <Group justify="space-between" align="center" mb="lg">
                <Title order={4} mb="xs">
                    Function Catalog
                </Title>
                <Button
                    variant="outline"
                    leftSection={<IconRefresh size={18} />}
                    onClick={handleRefresh}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Group>

            <Text size="sm" c="dimmed" mb="lg">
                View function catalog entries from the system.
            </Text>

            {error && (
                <Alert color="red" mb="md">
                    {error}
                </Alert>
            )}

            {loading ? (
                <LoadingState variant="centered" />
            ) : (
                <Paper withBorder p="md">
                    {functionCatalog.length === 0 ? (
                        <EmptyState title={`No ${getSingularLabel('FunctionCatalog')} found`} />
                    ) : (
                        <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                            <Table striped highlightOnHover stickyHeader>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th w={50}></Table.Th>
                                        <Table.Th><strong>Display Name</strong></Table.Th>
                                        <Table.Th><strong>ID</strong></Table.Th>
                                        <Table.Th><strong>Registered</strong></Table.Th>
                                        <Table.Th><strong>Target Entities</strong></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {functionCatalog.map((item, index) => {
                                        const isExpanded = expandedRows.has(index);
                                        const itemId = item.Id || item.id;
                                        const isSelected = selectedEntityId === itemId;
                                        return (
                                            <React.Fragment key={index}>
                                                <Table.Tr
                                                    ref={(el) => {
                                                        if (el) rowRefs.current.set(index, el);
                                                    }}
                                                    onClick={() => handleRowClick(item)}
                                                    style={{
                                                        cursor: itemId ? 'pointer' : 'default',
                                                        backgroundColor: isSelected ? rowHighlightBg : undefined
                                                    }}
                                                >
                                                    <Table.Td onClick={(e) => e.stopPropagation()}>
                                                        <ActionIcon
                                                            variant="subtle"
                                                            size="sm"
                                                            onClick={() => toggleRowExpansion(index)}
                                                        >
                                                            {isExpanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                                                        </ActionIcon>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text size="sm" fw={500}>
                                                            {item.DisplayName || 'N/A'}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text size="sm" ff="monospace" fz="xs">
                                                            {item.Id || 'N/A'}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Badge
                                                            size="sm"
                                                            color={item.Registered ? 'green' : 'gray'}
                                                            variant="light"
                                                        >
                                                            {item.Registered ? 'Yes' : 'No'}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {renderTargetEntities(item.TargetEntities)}
                                                    </Table.Td>
                                                </Table.Tr>
                                                <Table.Tr>
                                                    <Table.Td colSpan={5} py={0} style={{ borderBottom: isExpanded ? 1 : 0, verticalAlign: 'top' }}>
                                                        <Collapse in={isExpanded}>
                                                            <Box
                                                                p="md"
                                                                style={{
                                                                    backgroundColor: inputBoxBg
                                                                }}
                                                            >
                                                                <Text size="sm" fw={600} mb="xs">
                                                                    Inputs
                                                                </Text>
                                                                {renderInputs(item.Inputs)}
                                                            </Box>
                                                        </Collapse>
                                                    </Table.Td>
                                                </Table.Tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    )}
                </Paper>
            )}
        </Box>
    );
};
